import { useEffect, useRef, useState } from 'react';

export default function OurStory() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-16 md:py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-20 items-center">
          {/* Content Side */}
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
          }`}>
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="w-8 md:w-12 h-px bg-[#C9A86C]" />
              <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Our Story</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-[#0A0A0A] mb-6 md:mb-8 leading-tight">
              Built on a Foundation of
              <span className="block text-[#C9A86C]">Excellence</span>
            </h2>

            <div className="space-y-4 md:space-y-6 text-base md:text-lg text-[#4A4A4A] leading-relaxed">
              <p>
                BuildFast was founded in 2009 by a team of Nigerian engineers who had trained and worked across Europe, bringing back <strong className="text-[#0A0A0A]">world-class standards</strong> to the Nigerian construction industry.
              </p>
              
              <p>
                We saw a gap in the market—clients who wanted <strong className="text-[#0A0A0A]">luxury finishes</strong> but were frustrated by inconsistent quality and missed deadlines. We set out to change that narrative.
              </p>
              
              <p>
                Today, BuildFast is the <strong className="text-[#0A0A0A]">trusted partner</strong> for Nigeria's most discerning property owners, architects, and developers who refuse to compromise on execution.
              </p>
            </div>

            {/* Stats */}
            <div className="mt-8 md:mt-12 grid grid-cols-3 gap-4 md:gap-6">
              <div className="text-center p-4 md:p-6 bg-[#F8F6F3]">
                <div className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#C9A86C] mb-2">15+</div>
                <div className="text-xs md:text-sm text-[#6B6B6B] uppercase tracking-wider">Years</div>
              </div>
              <div className="text-center p-4 md:p-6 bg-[#F8F6F3]">
                <div className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#C9A86C] mb-2">200+</div>
                <div className="text-xs md:text-sm text-[#6B6B6B] uppercase tracking-wider">Projects</div>
              </div>
              <div className="text-center p-4 md:p-6 bg-[#F8F6F3]">
                <div className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#C9A86C] mb-2">98%</div>
                <div className="text-xs md:text-sm text-[#6B6B6B] uppercase tracking-wider">Satisfaction</div>
              </div>
            </div>
          </div>

          {/* Image Side */}
          <div className={`relative transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
          }`}>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Black%20African%20construction%20manager%20reviewing%20luxury%20interior%20blueprints%20in%20modern%20office%2C%20Nigerian%20engineer%20with%20architectural%20plans%2C%20professional%20business%20attire%2C%20warm%20natural%20lighting%2C%20high%20end%20workspace%2C%20editorial%20photography%20style%2C%20sophisticated%20neutral%20tones%2C%20shallow%20depth%20of%20field&width=600&height=750&seq=about002&orientation=portrait"
                alt="BuildFast Leadership"
                className="w-full h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] object-cover"
              />
              <div className="absolute -bottom-3 -left-3 md:-bottom-6 md:-left-6 w-24 md:w-48 h-24 md:h-48 bg-[#C9A86C] -z-10" />
              <div className="absolute -top-3 -right-3 md:-top-6 md:-right-6 w-16 md:w-32 h-16 md:h-32 border-2 border-[#0A0A0A] -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
